/* Version: 3.5.137 */
'use client';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { forwardRef, useState, useRef, useEffect } from 'react';
import { useSelect, useButton, HiddenSelect } from 'react-aria';
import { useSelectState } from 'react-stately';
import PropTypes from 'prop-types';
import { DROPDOWN_VARIANT } from '../Dropdown/constants.js';
import Errors from '../Errors/Errors.js';
import Label from '../Label/Label.js';
import i18n from '../../lang/i18n/index.js';
import { hasErrors, rem } from '../../utils/functions.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import { DEFAULT_INPUT_WIDTH } from './constants.js';
import ListBox from './ListBox.js';
import Popover from './Popover.js';
import { Root, InputWrapper, InputValue, Placeholder, IconWrapper, ArrowDownIcon, DashedUnderline } from './styledComponents.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import useLang from '../../hooks/useLang.js';
import useTheme from '../../hooks/useTheme.js';
import useDerivedState from '../../hooks/useDerivedState.js';
import useId from '../../hooks/useId.js';
import componentWithName from '../../prop-types/componentWithName.js';

var _excluded = ["callouts", "customStyle", "disabled", "readOnly", "required", "errors", "isNonModal", "id", "label", "name", "outline", "placeholder", "style", "tooltip", "variant"];
var Select = /*#__PURE__*/forwardRef(function (_ref, userRef) {
  var _ref$callouts = _ref.callouts,
    callouts = _ref$callouts === undefined ? false : _ref$callouts,
    _ref$customStyle = _ref.customStyle,
    customStyle = _ref$customStyle === undefined ? {
      rootStyle: {},
      labelStyle: {},
      inputStyle: {},
      inputWrapperStyle: {},
      menuStyle: {}
    } : _ref$customStyle,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === undefined ? false : _ref$disabled,
    _ref$readOnly = _ref.readOnly,
    readOnly = _ref$readOnly === undefined ? false : _ref$readOnly,
    _ref$required = _ref.required,
    required = _ref$required === undefined ? false : _ref$required,
    _ref$errors = _ref.errors,
    errors = _ref$errors === undefined ? [] : _ref$errors,
    _ref$isNonModal = _ref.isNonModal,
    isNonModal = _ref$isNonModal === undefined ? false : _ref$isNonModal,
    controlledId = _ref.id,
    label = _ref.label,
    name = _ref.name,
    _ref$outline = _ref.outline,
    outline = _ref$outline === undefined ? false : _ref$outline,
    placeholder = _ref.placeholder,
    style = _ref.style,
    tooltip = _ref.tooltip,
    _ref$variant = _ref.variant,
    variant = _ref$variant === undefined ? DROPDOWN_VARIANT.BASIC : _ref$variant,
    rest = _objectWithoutProperties(_ref, _excluded);
  var lang = useLang();
  var theme = useTheme();
  var _useDerivedState = useDerivedState(useId('select-'), controlledId),
    _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
    id = _useDerivedState2[0];
  var _useState = useState(DEFAULT_INPUT_WIDTH),
    _useState2 = _slicedToArray(_useState, 2),
    minWidth = _useState2[0],
    setMinWidth = _useState2[1];
  var _safeCustomStyle = safeCustomStyle(customStyle),
    rootStyle = _safeCustomStyle.rootStyle,
    inputWrapperStyle = _safeCustomStyle.inputWrapperStyle,
    inputStyle = _safeCustomStyle.inputStyle,
    menuStyle = _safeCustomStyle.menuStyle;
  var hasError = errors !== null && errors !== undefined && errors.length ? hasErrors(errors) : false;
  var errorMessage = hasError ? errors
  // Only return non-empty error messages
  .filter(function (e) {
    return typeof e === 'string' || e.msg !== '';
  }).map(function (e) {
    return typeof e === 'string' ? e : e.msg;
  }).join('. ') : null;
  var validationState = hasError ? 'invalid' : 'valid';

  // Reconstruct the props to use with react-aria
  var props = _objectSpread({
    isDisabled: disabled,
    isReadOnly: readOnly,
    isRequired: required,
    errorMessage: errorMessage,
    id: id,
    isNonModal: isNonModal,
    label: label,
    name: name,
    validationState: validationState
  }, rest);

  // Create state based on the incoming props
  var state = useSelectState(props);

  // Get props for child elements from useSelect
  var fallbackRef = useRef(null);
  var ref = userRef || fallbackRef;
  var _useSelect = useSelect(props, state, ref),
    labelProps = _useSelect.labelProps,
    triggerProps = _useSelect.triggerProps,
    valueProps = _useSelect.valueProps,
    menuProps = _useSelect.menuProps;
  var _useButton = useButton(triggerProps, ref),
    buttonProps = _useButton.buttonProps;

  // This is needed to have the same width on the popover and the input element when open
  useEffect(function () {
    var _ref$current;
    if (ref !== null && ref !== undefined && (_ref$current = ref.current) !== null && _ref$current !== undefined && _ref$current.clientWidth) {
      setMinWidth(ref.current.clientWidth);
    }
  }, [ref, state === null || state === undefined ? undefined : state.isOpen, state === null || state === undefined ? undefined : state.selectedKey]);
  return /*#__PURE__*/jsxs(Root, {
    rootStyle: rootStyle,
    style: _objectSpread({
      '--errors-width': rem(minWidth)
    }, style),
    children: [label && /*#__PURE__*/jsx(Label, _objectSpread(_objectSpread({
      customStyle: {
        labelStyle: customStyle.labelStyle
      },
      htmlFor: id,
      tooltip: tooltip
    }, labelProps), {}, {
      children: label
    })), /*#__PURE__*/jsx(HiddenSelect, {
      state: state,
      triggerRef: ref,
      label: label,
      name: name
    }), /*#__PURE__*/jsxs(InputWrapper, _objectSpread(_objectSpread({
      as: "button"
    }, buttonProps), {}, {
      "data-invalid": validationState === 'invalid' || outline,
      "data-disabled": disabled,
      "data-focused": state === null || state === undefined ? undefined : state.isFocused,
      "data-hasicon": true,
      "data-variant": variant,
      inputWrapperStyle: inputWrapperStyle,
      ref: ref,
      theme: theme,
      children: [/*#__PURE__*/jsx(InputValue, _objectSpread(_objectSpread({}, valueProps), {}, {
        cursor: "pointer",
        "data-disabled": disabled,
        inputStyle: inputStyle,
        theme: theme,
        children: state.selectedItem ? state.selectedItem.rendered : /*#__PURE__*/jsx(Placeholder, {
          theme: theme,
          children: placeholder || i18n[lang].select.placeholder
        })
      })), /*#__PURE__*/jsx(IconWrapper, {
        "data-isopen": state.isOpen,
        isClickable: true,
        children: /*#__PURE__*/jsx(ArrowDownIcon, {})
      }), variant === DROPDOWN_VARIANT.BASIC_LINE_DASHED ? /*#__PURE__*/jsx(DashedUnderline, {
        "aria-hidden": true,
        theme: theme
      }) : null]
    })), (state === null || state === undefined ? undefined : state.isOpen) && /*#__PURE__*/jsx(Popover, {
      isNonModal: isNonModal,
      placement: "bottom start",
      state: state,
      triggerRef: ref,
      theme: theme,
      children: /*#__PURE__*/jsx(ListBox, _objectSpread(_objectSpread({}, menuProps), {}, {
        menuStyle: menuStyle,
        minWidth: minWidth,
        state: state
      }))
    }), errors !== null && errors !== undefined && errors.length ? /*#__PURE__*/jsx(Errors, {
      callouts: callouts,
      errors: errors
    }) : null]
  });
});
process.env.NODE_ENV !== "production" ? Select.propTypes = {
  /**
   * Describes the type of autocomplete functionality the input should provide if any. Maps to the HTML [autocomplete](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete) attribute.
   */
  autoComplete: PropTypes.string,
  /**
   * Whether the element should receive focus on render. Maps to the HTML [autofocus](https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/autofocus) attribute.
   */
  autoFocus: PropTypes.bool,
  /**
   * Sets `aria-atomic=true`, `aria-live=polite` and `role=alert` on error messages (ensures errors are announced by screen readers as soon as they happen).
   */
  callouts: PropTypes.bool,
  /**
   * Style overrides
   */
  customStyle: PropTypes.shape({
    rootStyle: PropTypes.object,
    // root wrapper
    labelStyle: PropTypes.object,
    // label
    inputStyle: PropTypes.object,
    // input
    inputWrapperStyle: PropTypes.object,
    // input wrapper
    menuStyle: PropTypes.object // popover menu (non-native only)
  }),
  /**
   * Sets the default open state of the menu.
   */
  defaultOpen: PropTypes.bool,
  /**
   * The initial selected key in the collection (uncontrolled).
   */
  defaultSelectedKey: PropTypes.string,
  /**
   * Whether the input is disabled. Maps to the HTML [disabled](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/disabled) attribute.
   */
  disabled: PropTypes.bool,
  /**
   * An array of error messages to be displayed. Passing an array also enables error styling.
   */
  errors: PropTypes.arrayOf(PropTypes.string),
  /**
   * Whether the open menu is non-modal, i.e. elements outside the `Select` may be interacted with by
   * assistive technologies.
   *
   * Most scenarios should not use this option as it may negatively impact the screen reader experience.
   */
  isNonModal: PropTypes.bool,
  /**
   * Sets the open state of the menu.
   */
  isOpen: PropTypes.bool,
  /**
   * The list of Select items (controlled).
   */
  items: PropTypes.arrayOf(PropTypes.object),
  /**
   * Label for the input.
   */
  label: PropTypes.string,
  /**
   * The name of the input, used when submitting an HTML form.
   */
  name: PropTypes.string,
  /**
   * Method that is called when the open state of the menu changes.
   */
  onOpenChange: PropTypes.func,
  /**
   * Handler that is called when the selection changes.
   */
  onSelectionChange: PropTypes.func,
  /**
   * Flag to set error styling without displaying errors; useful if handling errors yourself using the [Errors](/docs/errors) component.
   */
  outline: PropTypes.bool,
  /**
   * Temporary text that occupies the text input when it is empty.
   */
  placeholder: PropTypes.string,
  /**
   * Whether the input can be selected but not changed by the user.
   */
  readOnly: PropTypes.bool,
  /**
   * Whether user input is required on the input before form submission.
   */
  required: PropTypes.bool,
  /**
   * The currently selected key in the collection (controlled).
   */
  selectedKey: PropTypes.string,
  /**
   * [Tooltip](/docs/tooltip) displayed beside the label
   */
  tooltip: componentWithName('Tooltip'),
  /**
   * Specifies which style of input to use.
   */
  variant: PropTypes.oneOf([DROPDOWN_VARIANT.BASIC, DROPDOWN_VARIANT.BASIC_LINE, DROPDOWN_VARIANT.BASIC_LINE_DASHED])
} : undefined;
process.env.NODE_ENV !== "production" ? Select.displayName = 'Select' : undefined;

export { Select as default };
